import GuidePage from "./GuidePage";

const chapters = [
  {
    title: "Womit anfangen? Das erste Bild finden.",
    body: (
      <>
        <p>
          Eine Trauerrede beginnt nicht bei den Daten. Sie beginnt bei einem Bild, das Sie ganz genau
          im Kopf haben, wenn Sie an den verstorbenen Menschen denken. Ein Fenster, das immer offen war.
          Eine Hand auf Ihrer Schulter. Ein Satz, den er oder sie so gesagt hat, wie kein anderer Mensch.
        </p>
        <p>
          Suchen Sie dieses eine Bild — und schreiben Sie es in einem einzigen Satz auf. Alles Weitere
          wird sich um diesen einen Satz herum ordnen. Wer bei den Lebensdaten anfängt, verliert die Rede.
          Wer bei einem konkreten Detail anfängt, gewinnt den Raum.
        </p>
      </>
    ),
  },
  {
    title: "Wie lang sollte eine Trauerrede sein?",
    body: (
      <>
        <p>
          Die häufigste Sorge ist: „Zu kurz.“ Die häufigste Wahrheit ist: „Zu lang.“
          Eine gute Trauerrede dauert selten mehr als sieben Minuten. Für nahe Angehörige oft drei bis fünf.
          Für Kolleg:innen und Freunde eher zwei bis drei.
        </p>
        <p>
          Sprechen Sie den Text laut vor sich selbst — die tatsächliche Sprechzeit ist meist länger, als
          man denkt. Faustregel: <strong>150 Wörter ≈ 1 Minute ruhig gesprochen.</strong>
        </p>
      </>
    ),
  },
  {
    title: "Wie beginne ich? Der erste Satz.",
    body: (
      <>
        <p>
          Beginnen Sie nicht mit „Wir haben uns hier versammelt, um…“. Das ist Anlass, nicht Inhalt.
          Beginnen Sie stattdessen mit dem einen Bild aus Schritt 1 — dem konkreten, das nur zu dieser
          einen Person passt.
        </p>
        <p className="italic text-ink">
          Beispiel: „Wenn ich an sie denke, denke ich an das Küchenfenster. Es war das erste, das
          morgens auf war.“
        </p>
        <p>
          Ein solcher Satz nimmt den Raum sofort mit, weil er konkret ist. Alles Allgemeine — „ein
          großherziger Mensch“, „immer da für andere“ — klingt in einer Trauerrede leer. Bilder klingen wahr.
        </p>
      </>
    ),
  },
  {
    title: "Welche Struktur trägt eine Trauerrede?",
    body: (
      <>
        <p>Eine tragende Struktur hat vier Teile — schlicht, aber wirksam:</p>
        <ol className="mt-4 space-y-2 list-decimal list-inside">
          <li><strong>Ein konkretes Bild.</strong> Eine Geste, ein Ort, ein Satz.</li>
          <li><strong>Wer sie oder er war.</strong> Zwei bis drei prägende Züge — keine Aufzählung, sondern Beispiele.</li>
          <li><strong>Was bleibt.</strong> Was nehmen die Trauernden mit? Ein Satz, eine Haltung, eine Art zu leben.</li>
          <li><strong>Ein leiser Schluss.</strong> Ein Dank, ein Bild, ein einziger Satz, der ruhig trägt.</li>
        </ol>
        <p className="mt-6">
          Diese vier Teile brauchen keine sichtbaren Kapitel — sie sind das innere Gerüst, das den Fluss
          hält, damit die Rede nicht in Anekdoten zerbricht.
        </p>
      </>
    ),
  },
  {
    title: "Wie gehe ich mit Emotionen um?",
    body: (
      <>
        <p>
          Sie dürfen weinen. Sie dürfen Pausen machen. Sie dürfen absetzen. Eine Trauerrede muss nicht
          fließend gehalten werden — sie muss ehrlich gehalten werden.
        </p>
        <p>
          Wenn Sie befürchten, in der Mitte zu brechen: Schreiben Sie kurze Sätze. Lange Sätze sind
          schwer bei Tränen. Kurze Sätze halten Sie über die Zeile. Wenn Sie an einer Stelle nicht
          weiterkönnen: Trinken Sie einen Schluck Wasser, atmen Sie einmal ruhig — der Raum wird auf Sie warten.
        </p>
      </>
    ),
  },
  {
    title: "Welche Fehler häufig passieren.",
    body: (
      <>
        <ul className="space-y-3 list-disc list-inside">
          <li><strong>Zu viele Anekdoten.</strong> Zwei bis drei genügen. Mehr verwässert.</li>
          <li><strong>Superlative und Pathos.</strong> „Die beste Mutter der Welt“ berührt weniger als ein konkretes Bild.</li>
          <li><strong>Ratschläge an die Trauernden.</strong> „Wir müssen jetzt stark sein“ — das ist eine Bürde, kein Trost.</li>
          <li><strong>Zu lang.</strong> Die Rede darf nie länger sein, als der Redner sie tragen kann.</li>
          <li><strong>Ausgesprochene Kritik am Verstorbenen.</strong> Was zu Lebzeiten unausgesprochen blieb, gehört nicht in eine Trauerrede.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Wie proben — oder eine andere Person sprechen lassen.",
    body: (
      <>
        <p>
          Lesen Sie die Rede zwei- bis dreimal laut vor sich selbst. Nicht auswendig lernen — vertraut werden.
          Markieren Sie sich Atempausen. Streichen Sie Wörter, die stolpern.
        </p>
        <p>
          Wenn Sie unsicher sind, ob Sie es aushalten: Bitten Sie eine nahestehende Person, den Text zur
          Not zu übernehmen. Geben Sie ihm oder ihr den Text am Vortag. Nichts ist unwürdiger, als eine
          Rede abzubrechen, weil kein Plan B da war — und nichts würdiger, als eine Rede, die getragen
          wird, auch wenn sie nicht aus dem eigenen Mund kommt.
        </p>
      </>
    ),
  },
  {
    title: "Am Tag selbst — die letzten praktischen Tipps.",
    body: (
      <>
        <ul className="space-y-3 list-disc list-inside">
          <li>Rede in doppelter Kopie ausdrucken — eine für Sie, eine in Reserve bei einer Vertrauensperson.</li>
          <li>Wasser griffbereit stellen.</li>
          <li>Wenn möglich, den Ort vorher einmal betreten, um die Akustik einzuschätzen.</li>
          <li>Vor dem Beginn drei tiefe Atemzüge. Nicht mehr, nicht weniger.</li>
          <li>Sprechen Sie zu einer bestimmten Person im Raum — jemandem, den Sie gut kennen. Das hilft der Stimme.</li>
        </ul>
        <p className="mt-6">
          Und danach: setzen Sie sich hin. Sie haben getragen, was zu tragen war.
        </p>
      </>
    ),
  },
];

const faq = [
  {
    q: "Wie lang sollte eine Trauerrede sein?",
    a: "Für nahe Angehörige meist 3-5 Minuten, für Freunde und Kollegen 2-3 Minuten. Eine gute Trauerrede überschreitet selten sieben Minuten. Faustregel: 150 Wörter entsprechen etwa einer Minute ruhig gesprochener Rede.",
  },
  {
    q: "Wie beginnt man eine Trauerrede am besten?",
    a: "Nicht mit „Wir haben uns hier versammelt…“. Beginnen Sie mit einem konkreten Bild, das nur zu dieser einen Person passt — eine Geste, ein Satz, ein Ort. Konkretes trägt eine Rede, Allgemeines schwächt sie.",
  },
  {
    q: "Darf man in einer Trauerrede humorvoll sein?",
    a: "Ja, wenn es zur verstorbenen Person gepasst hätte. Ein leises Lachen an der richtigen Stelle ist Erinnerung, nicht Respektlosigkeit. Vermeiden Sie Pointen — bevorzugen Sie warme Anekdoten.",
  },
  {
    q: "Was tun, wenn ich beim Sprechen weine?",
    a: "Machen Sie eine Pause, trinken Sie einen Schluck Wasser, atmen Sie einmal ruhig. Der Raum wartet auf Sie. Kurze Sätze helfen — sie sind bei Tränen leichter zu halten als lange.",
  },
  {
    q: "Kann jemand anders die Rede vortragen, wenn ich es nicht schaffe?",
    a: "Ja — und das ist völlig würdig. Geben Sie den Text am Vortag einer nahestehenden Person, die ihn übernehmen könnte. Der Entwurf ist so geschrieben, dass er auch aus einem anderen Mund trägt.",
  },
  {
    q: "Muss ich die Trauerrede auswendig können?",
    a: "Nein — im Gegenteil. Lesen Sie sie ab. Auswendigkönnen ist am Trauertag eine unnötige Bürde. Wichtig ist, dass Sie den Text zwei- bis dreimal laut gelesen haben und ihn kennen.",
  },
];

const related = [
  { label: "Wie lang darf eine Trauerrede sein?", to: "/wie-lang-trauerrede" },
  { label: "Die richtige Einleitung", to: "/trauerrede-einleitung" },
  { label: "Eine Trauerrede halten", to: "/trauerrede-halten" },
  { label: "Für die Mutter", to: "/trauerrede-mutter" },
  { label: "Für den Vater", to: "/trauerrede-vater" },
];

export default function TrauerredeSchreiben() {
  return (
    <GuidePage
      slug="trauerrede-schreiben"
      metaTitle="Trauerrede schreiben — in 8 Schritten. Ohne Pathos. | Oratiorede"
      metaDescription="Wie schreibt man eine Trauerrede? Ein ruhiger, ehrlicher Leitfaden in 8 Schritten — vom ersten Bild bis zum Vortrag. Plus Vorlage-Entwurf ab 29 €."
      eyebrow="Ratgeber · Trauerrede schreiben"
      headline={
        <>
          Eine Trauerrede schreiben.
          <br />
          <span className="italic text-umber">In 8 Schritten. Ohne Pathos.</span>
        </>
      }
      intro="Es gibt keinen Zaubertrick für gute Trauerreden. Aber es gibt einen Weg, den viele erfahrene Trauerredner:innen kennen — konkret statt allgemein, ruhig statt pathetisch, kurz statt vollständig. Hier ist er, in acht ehrlichen Schritten."
      chapters={chapters}
      faq={faq}
      related={related}
    />
  );
}
